import { useConnections } from '@studio/api-client';
import type { ConnectionRecord, PlatformId } from '@studio/contract';
import { Button, Card, CardBody, CardDescription, CardHeader, CardTitle, Chip } from '@studio/ui';
import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { BrandDnaModal } from '../components/BrandDnaModal';
import { humanizeError } from '../lib/error-copy';
import { track } from '../observability';

/**
 * Connections — Surface 3. Lists every platform we plan to integrate and
 * surfaces its binding state for the active workspace.
 *
 * Per-platform UX states (ADR 0004):
 * - **Connected** — a `ConnectionRecord` exists in `/api/connections`
 *   for this `platformId`. Card shows the external account label and a
 *   Disconnect button (revoke flow lands in iter 44).
 * - **Connect** — the platform adapter is shipped (Wix, post-iter 41)
 *   but no binding exists. Card shows a "Connect" CTA that kicks off
 *   the install flow.
 * - **Coming soon** — adapter hasn't shipped. Card shows disabled
 *   "Connect" + a "Coming soon" chip.
 *
 * Iter 40 only ships the listing endpoint + Connected state; the install
 * URL (which flips "Coming soon" → "Connect") lands in iters 41-44.
 */

interface PlatformDef {
  id: Extract<PlatformId, 'wix' | 'etsy' | 'ebay' | 'shopify'>;
  name: string;
  blurb: string;
  /** True once the per-platform OAuth flow is implemented (iter 41+ for Wix). */
  ready: boolean;
}

const PLATFORMS: PlatformDef[] = [
  {
    id: 'wix',
    name: 'Wix',
    blurb:
      'Import the catalog from your Wix store and push generated images back to product galleries.',
    ready: false,
  },
  {
    id: 'etsy',
    name: 'Etsy',
    blurb: 'Pull listings, refresh hero shots, generate lifestyle scenes for handmade goods.',
    ready: false,
  },
  {
    id: 'ebay',
    name: 'eBay',
    blurb: 'Sync active listings and lift photos with auto-enhanced backgrounds.',
    ready: false,
  },
  {
    id: 'shopify',
    name: 'Shopify',
    blurb: 'Connect a Shopify store via the Admin API. Q3 target.',
    ready: false,
  },
];

type CardState =
  | { kind: 'connected'; record: ConnectionRecord }
  | { kind: 'connect' }
  | { kind: 'soon' };

function deriveState(
  platform: PlatformDef,
  byPlatform: Map<PlatformId, ConnectionRecord>,
): CardState {
  const record = byPlatform.get(platform.id);
  if (record && record.status === 'active') return { kind: 'connected', record };
  if (platform.ready) return { kind: 'connect' };
  return { kind: 'soon' };
}

export function ConnectionsPage() {
  const [brandOpen, setBrandOpen] = useState(false);
  const connections = useConnections();

  useEffect(() => {
    track('connections.viewed', {});
  }, []);

  const byPlatform = useMemo(() => {
    const map = new Map<PlatformId, ConnectionRecord>();
    for (const record of connections.data?.items ?? []) {
      // First match wins — a workspace can have multiple installs per
      // platform in theory; iter 40 surfaces only the most-recent active
      // one per card.
      if (!map.has(record.platformId)) map.set(record.platformId, record);
    }
    return map;
  }, [connections.data]);

  return (
    <AppShell activeTab="connections" onOpenBrand={() => setBrandOpen(true)}>
      <header className="mb-8">
        <p className="eyebrow mb-2">Surface 3 · Sources</p>
        <h1 className="display text-display-xl">Connect your platforms</h1>
        <p className="mt-1 max-w-xl text-sm text-fg-3">
          Studio reads from these to keep your catalog in sync. OAuth flows go live as each adapter
          ships.
        </p>
      </header>

      {connections.error ? (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-danger/40 bg-danger/10 p-4 text-sm text-danger"
        >
          {humanizeError(connections.error)}
        </div>
      ) : null}

      {connections.isPending ? (
        <PlatformSkeletons />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PLATFORMS.map((platform) => {
            const state = deriveState(platform, byPlatform);
            return (
              <Card key={platform.id} className="overflow-hidden">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle className="text-base">{platform.name}</CardTitle>
                    <PlatformChip state={state} />
                  </div>
                  <CardDescription className="text-xs text-fg-3">
                    {state.kind === 'connected'
                      ? `Connected as ${state.record.externalAccountLabel}.`
                      : platform.blurb}
                  </CardDescription>
                </CardHeader>
                <CardBody className="pt-0">
                  <PlatformCta platform={platform} state={state} />
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <BrandDnaModal open={brandOpen} onOpenChange={setBrandOpen} />
    </AppShell>
  );
}

/**
 * Loading-state skeleton (iter 92). Matches the live grid 1:1 so the
 * page doesn't reflow when data arrives. `aria-busy` + `aria-label`
 * announce the loading state to assistive tech.
 */
function PlatformSkeletons() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      aria-busy="true"
      aria-label="Loading platform connections"
    >
      {PLATFORMS.map((p) => (
        <Card key={p.id} className="overflow-hidden">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="h-4 w-20 animate-pulse rounded bg-surface-2" />
              <div className="h-5 w-16 animate-pulse rounded-full bg-surface-2" />
            </div>
            <div className="mt-2 space-y-1.5">
              <div className="h-3 w-full animate-pulse rounded bg-surface-2" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-surface-2" />
            </div>
          </CardHeader>
          <CardBody className="pt-0">
            <div className="h-8 w-24 animate-pulse rounded bg-surface-2" />
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function PlatformChip({ state }: { state: CardState }) {
  if (state.kind === 'connected') {
    return (
      <Chip size="xs" variant="solid" tone="success">
        Connected
      </Chip>
    );
  }
  if (state.kind === 'connect') {
    return (
      <Chip size="xs" variant="soft" tone="accent">
        Available
      </Chip>
    );
  }
  return (
    <Chip size="xs" variant="soft" tone="neutral">
      Coming soon
    </Chip>
  );
}

function PlatformCta({ platform, state }: { platform: PlatformDef; state: CardState }) {
  if (state.kind === 'connected') {
    return (
      <Button variant="ghost" size="sm" disabled aria-label={`Disconnect ${platform.name}`}>
        Disconnect
      </Button>
    );
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={state.kind === 'soon'}
      aria-label={`Connect ${platform.name}`}
    >
      Connect
    </Button>
  );
}
